import {Checkbox, FormControlLabel} from '@mui/material';
import {useEffect, useState} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {useDispatch, useSelector} from 'react-redux';
import CustomisedButton from '../../components/button/button';
import CustomisedCardLine from '../../components/card/cardLine';
import TemplateLoanCard from '../../components/card/loanCard/TemplateLoanCard';
import SubscriptionCard from '../../components/subscriptionCard/SubscriptionCard';
import ElementTitle from '../../components/title/elementTitle/elementTitle';
import {IGetLoanTemplatesResponse} from '../../models/loan.model';
import {TFeePaymentType} from '../../models/registration.model';
import {setHeaderTitle} from '../../redux/headerTitle/headerTitleSlice';
import {setSubscriptionPlanId} from '../../redux/subscriptionPlan/subscriptionPlanSlice';
import {GetLoanTemplates} from '../../services/loan.service';
import AdditionalInfoSkeleton from './additioalInfoSkeleton';
import {IAdditionalInfo} from './IAdditionalInfo';
import {RootState} from '../../redux/store';

export default function AdditionalInfo({
    setInnerState,
    content,
    primaryTermsServiceLoading,
}: IAdditionalInfo) {
    const queryClient = useQueryClient();
    const [loanTemplates, setLoanTemplates] = useState<
        IGetLoanTemplatesResponse[]
    >([]);
    const [disableBtn, setDisableBtn] = useState(true);
    const [selectedFeePaymentType, setSelectedFeePaymentType] =
        useState<TFeePaymentType>();
    const dispatch = useDispatch();
    const {mutate: GetLoanTemplatesMutate, isLoading} = useMutation(
        GetLoanTemplates,
        {
            onSuccess: (data: IGetLoanTemplatesResponse[]) => {
                if (data) {
                    setLoanTemplates(data);
                }
            },
            onError: () => {},
        }
    );
    useEffect(() => {
        setSelectedFeePaymentType(
            content?.feePaymentMethods[0]?.feePaymentType
        );
        dispatch(
            setSubscriptionPlanId(content?.feePaymentMethods[0]?.feePaymentType)
        );
        GetLoanTemplatesMutate({
            loanTemplateKey: queryClient.getQueryData('LOAN_TEMPLATE_KEY'),
        });
        dispatch(
            setHeaderTitle({
                header: 'تایید درخواست تسهیلات',
                color: 'var(--header-color)',
                parentRoute: null,
            })
        );
    }, []);
    const handleContinue = () => {
        setInnerState('basickInfo');
    };

    const handleSelectSubscriptionPlan = (feePaymentType: TFeePaymentType) => {
        setSelectedFeePaymentType(feePaymentType);
        dispatch(setSubscriptionPlanId(feePaymentType));
    };

    if (isLoading || primaryTermsServiceLoading) {
        return <AdditionalInfoSkeleton />;
    }
    return (
        <div
            style={{
                height: 'calc(-64px + 100svh)',
                overflowY: 'auto',
                paddingTop: '15px',
                paddingBottom: '60px',
            }}
        >
            <div className="flex flex-col px-5">
                <TemplateLoanCard
                    description={loanTemplates[0]?.description}
                    title={loanTemplates[0]?.title}
                    logo={loanTemplates[0]?.logoUrl}
                />

                <div className="text-[var(--primary-text-color)] text-justify text-sm my-5 font-bold px-2">
                    ضمن بررسی اطلاعات زیر، در صورت موافقت با شرایط تسهیلات نسبت
                    به ثبت درخواست اقدام نمائید.
                </div>

                {(content?.durationInfo ||
                    content?.organizationTitle ||
                    content?.financialSupplierTitle) && (
                    <div className="mb-5">
                        <CustomisedCardLine
                            itemList={[
                                ...(content?.organizationTitle
                                    ? [
                                          {
                                              title: 'نام سازمان',
                                              value: content.organizationTitle,
                                          },
                                      ]
                                    : []),
                                ...(content?.financialSupplierTitle
                                    ? [
                                          {
                                              title: 'منبع تامین مالی',
                                              value: content?.financialSupplierTitle,
                                          },
                                      ]
                                    : []),
                                ...(content?.durationInfo
                                    ? [
                                          {
                                              title: 'مبلغ تسهیلات',
                                              value: `${content?.calculationParams?.loanAmount.toLocaleString('fa')} ریال`,
                                          },
                                      ]
                                    : []),
                                ...(content?.durationInfo
                                    ? [
                                          {
                                              title: 'مبلغ قسط',
                                              value: `${content?.calculationParams?.installmentAmount.toLocaleString('fa')} ریال`,
                                          },
                                      ]
                                    : []),
                                ...(content?.durationInfo
                                    ? [
                                          {
                                              title: 'مدت بازپرداخت',
                                              value: content?.durationInfo,
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    </div>
                )}

                {content?.calculationParams?.feeAmount !== 0 && (
                    <>
                        <div className="mb-3">
                            <ElementTitle
                                title={`برای دریافت این تسهیلات، خرید اشتراک کمــان\u200Cلند ضروری است.`}
                            />
                        </div>
                        <div className="bg-[var(--input-bg-color)] p-4 rounded-lg mb-5 border border-[var(--border-color)]">
                            <div className="flex flex-col gap-[15px]">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-[var(--tirnary-text-color)]">
                                        هزینه اشتراک کمان{'\u200C'}لند
                                    </span>
                                    <span className="text-sm text-[var(--primary-text-color)] font-semibold">
                                        {content?.calculationParams?.feeAmount.toLocaleString(
                                            'fa'
                                        ) || 0}{' '}
                                        ریال
                                    </span>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {content.feePaymentMethods?.length > 0 && (
                    <>
                        <div className="mb-3">
                            <ElementTitle title="روش‌های خرید اشتراک" />
                        </div>
                        <div className="flex flex-col gap-4">
                            {content.feePaymentMethods?.map((item, index) => (
                                <SubscriptionCard
                                    key={index}
                                    title={item.title}
                                    description={item.description}
                                    className={`p-4 cursor-pointer border transition-transform duration-150 ${selectedFeePaymentType === item?.feePaymentType ? 'border-[#BBA57A] !bg-[#F4F0EA] shadow-lg' : 'border-none'}`}
                                    onClick={() =>
                                        handleSelectSubscriptionPlan(
                                            item?.feePaymentType
                                        )
                                    }
                                />
                            ))}
                        </div>
                    </>
                )}
                {content?.htmlTerms && (
                    <div
                        className=" 
                 overflow-y-auto 
                 rich-text
                 mt-4
                "
                    >
                        <div
                            className="text-sm text-[var(--primary-text-color)] text-justify leading-7 px-2"
                            dangerouslySetInnerHTML={{
                                __html: content?.htmlTerms || '',
                            }}
                        />
                    </div>
                )}
                <div className="mt-5">
                    <FormControlLabel
                        className="contract-formLable "
                        control={
                            <Checkbox
                                onChange={(e) => {
                                    setDisableBtn(!e.target.checked);
                                }}
                                sx={{
                                    color: 'var(--header-border-color)',
                                    '&.Mui-checked': {
                                        color: '#BBA57A',
                                    },
                                }}
                            />
                        }
                        label={
                            <div className="text-[0.875rem] text-[var(--primary-text-color)] font-bold px-2">
                                با اطــلاع از تمامی شــرایط درخواست دریافت
                                تسهیـــلات با مشخصات فوق را دارم.
                            </div>
                        }
                    />
                </div>
                <div className="py-5">
                    <div>
                        <CustomisedButton
                            title="ادامه"
                            disable={
                                disableBtn || selectedFeePaymentType === null
                            }
                            onClick={handleContinue}
                            type="button"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
